'use client'

import React, { useEffect, useState } from 'react'

import { ethers } from 'ethers'
import { HYPERBEAT_TOKEN_ADDRESS } from '@hyperswap/addresses'
import { useActiveWeb3React } from '../../../../hooks'
import { formatUnits } from 'ethers/lib/utils'
import styled from 'styled-components'
import Tag from 'components/Tag'

const HistoryTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px 20px;
  gap: 12px;
`

const HistoryItem = styled.div`
  padding: 12px;
  display: flex;
  justify-content: space-between;
  background: ${({ theme }) => theme.bg1};
  border-radius: 14px;

  .receive {
    color: ${({ theme }) => theme.text2};
  }

  .receive-amount {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.text1};
  }

  .receive-description {
    color: ${({ theme }) => theme.text2};
    font-size: 12px;
  }
`

interface WithdrawalRequest {
  id: string
  date: {
    year: number
    month: number
    day: number
  }
  amount: string
  formattedAmount: string
}

interface WithdrawalStatusProps {
  tokenDecimals?: number
  tokenSymbol?: string
  displayDecimals?: number
  refetchWithdrawals?: boolean
  setRefetchWithdrawals?: (value: boolean) => void
}

const vaultAbi = [
  'function getClaimableAmountByReceiver(uint256 year, uint256 month, uint256 day, address receiverAddr) view returns (uint256)',
]

const WithdrawalStatus = ({
  tokenDecimals = 18,
  tokenSymbol = 'hbHYPE',
  displayDecimals = 6,
  refetchWithdrawals,
  setRefetchWithdrawals,
}: WithdrawalStatusProps) => {
  const { account, library } = useActiveWeb3React()
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkWithdrawalStatus = async () => {
      if (!library || !account) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        const vaultContractInstance = new ethers.Contract(HYPERBEAT_TOKEN_ADDRESS, vaultAbi, library)

        const today = new Date()
        const year = today.getFullYear()
        const month = today.getMonth() + 1
        const day = today.getDate()

        const claimableAmount: bigint = await vaultContractInstance.getClaimableAmountByReceiver(
          year,
          month,
          day,
          account
        )

        if (claimableAmount > BigInt(0)) {
          const formattedAmount = parseFloat(formatUnits(claimableAmount, tokenDecimals)).toFixed(displayDecimals)

          setWithdrawalRequests([
            {
              id: `${year}-${month}-${day}`,
              date: {
                year,
                month,
                day,
              },
              amount: claimableAmount.toString(),
              formattedAmount,
            },
          ])
        } else {
          setWithdrawalRequests([])
        }
      } catch (error) {
        console.error('Error checking withdrawal status:', error)
        setWithdrawalRequests([])
      } finally {
        setLoading(false)
        if (refetchWithdrawals && setRefetchWithdrawals) {
          setRefetchWithdrawals(false)
        }
      }
    }

    checkWithdrawalStatus()
  }, [library, account, tokenDecimals, refetchWithdrawals, displayDecimals, setRefetchWithdrawals])

  if (loading) {
    return null
  }

  if (withdrawalRequests.length === 0) {
    return null
  }

  return (
    <HistoryTableContainer>
      <span style={{ fontWeight: 800, fontSize: '16px' }}>Pending Withdrawals</span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {withdrawalRequests.map((request) => (
          <HistoryItem key={request.id}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '14px' }} className="receive">
                You will receive by {request.date.month}/{request.date.day + 1}/{request.date.year}
              </div>
              <div className="receive-amount">
                {request.formattedAmount} {tokenSymbol}
              </div>
              <span className="receive-description">Processing automatically - no action required</span>
            </div>

            <Tag variant={1}>Pending</Tag>
          </HistoryItem>
        ))}
      </div>
    </HistoryTableContainer>
  )
}

export default WithdrawalStatus
